import type { HeadersFunction, LoaderFunction, MetaFunction } from '@remix-run/node';
import { redirect } from '@remix-run/node';
import { type ClientLoaderFunctionArgs, useOutletContext, useRouteLoaderData } from '@remix-run/react';
import type { BasicNode, TRootElement } from '@shared/dream-components';
import type { PostTheme } from '@shared/dream-components/src';

import BlueskyEmbed from '~/components/Scripts/BlueskyEmbed';
import StocktwitsEmbed from '~/components/Scripts/StocktwitsEmbed';
import TwitterEmbed from '~/components/Scripts/TwitterEmbed';
import StaticPage from '~/components/StaticPage';
import { PostPageContext } from '~/context/PostPageContext';
import { PostPageProvider } from '~/context/PostPageProvider';
import useCreatePageView from '~/hooks/useLoaders/useCreatePageView';
import useSetLastViewedResource from '~/hooks/useSetLastViewedResource';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import type { OutletContext } from '~/interfaces/outlet_context';
import type { Page } from '~/interfaces/page';
import type { Paywall } from '~/interfaces/paywall';
import type { Post as IPost, PostMetaInformation } from '~/interfaces/post';
import type { PostDynamicContentConditions } from '~/interfaces/post_dynamic_content_conditions';
import type { PostMergeTags } from '~/interfaces/post_merge_tags';
import type { Publication, WebThemeV2 } from '~/interfaces/publication';
import type { Site } from '~/interfaces/site';
import type { Subscription } from '~/interfaces/subscriptions';
import getPage from '~/services/api/pages/getPage.server';
import getPaywall from '~/services/api/paywalls/getPaywall.server';
import getDraftPost from '~/services/api/posts/getDraftPost.server';
import getPost from '~/services/api/posts/getPost.server';
import getPreviewSubscription from '~/services/api/preview_subscription/getPreviewSubscription.server';
import getPublication from '~/services/api/publication/getPublication.server';
import getSite from '~/services/api/sites/getSite.server';
import { getRenderableFontsCss } from '~/services/fonts';
import type { GoogleFontEntryType } from '~/services/fonts/cache';
import { loaderSession } from '~/session';
import getCanonicalUrl from '~/utils/getCanonicalUrl';
import getMergedMetaData from '~/utils/getMergedMetaData';
import getMetaDataLinks from '~/utils/getMetaDataLinks';
import getSameAsArray from '~/utils/getSameAsArray';
import setCacheHeaders from '~/utils/setCacheHeaders/index.server';
import { shouldRevalidateLoaderData } from '~/utils/shouldRevalidateLoaderData';

import useClickTracking from './_hooks/useClickTracking';
import { fetchDynamicContentConditions } from './_utils/fetchDynamicContentConditions';
import { fetchMergeTags } from './_utils/fetchMergeTags';
import { fetchPostMetaInformation } from './_utils/fetchPostMetaInformation';
import { processContentGate } from './_utils/processContentGate';

export const meta: MetaFunction<typeof loader> = ({ matches, data }) => {
  const post = data?.post;
  const publication = data?.publication;
  const requestUrl = data?.requestUrl || '';

  if (!post || !publication) {
    return [
      { title: '404 - Not Found' },
      { description: 'The newsletter platform built for growth.' },
      { keywords: 'Newsletter, Email, Growth, Platform, SaaS, Creator' },
    ];
  }

  return getMergedMetaData({
    canonicalUrl: getCanonicalUrl(requestUrl),
    matches,
    returnEarlyCheck: !post,
    overrides: {
      title: post?.meta_default_title || '',
      description: post?.meta_default_description || '',
      'og:url': `${publication?.url || ''}p/${post?.slug || ''}`,
      'og:title': post?.meta_og_title || '',
      'og:description': post?.meta_og_description || '',
      'og:site_name': publication?.name || '',
      'og:image': post?.image_url || publication?.thumbnail?.url || publication?.logo?.url || '',
      'og:image:alt': post?.web_subtitle || publication?.description || '',
      'twitter:url': `${publication?.url || ''}p/${post?.slug || ''}`,
      'twitter:title': post?.meta_twitter_title || '',
      'twitter:description': post?.meta_twitter_description || '',
      'twitter:image': post?.image_url || publication?.thumbnail?.url || publication?.logo?.url || '',
    },
    links: getMetaDataLinks({ renderableFonts: data?.renderableFonts || [] }),
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      identifier: post?.slug || '',
      url: requestUrl,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': requestUrl,
      },
      headline: post?.web_title || '',
      description: post?.web_subtitle || '',
      datePublished: post?.override_scheduled_at || '',
      dateModified: post?.updated_at || '',
      isAccessibleForFree: !(post?.hide_premium_post ?? false),
      image: post?.image_url
        ? {
            '@type': 'ImageObject',
            url: post.image_url,
          }
        : undefined,
      author: post?.authors?.[0]
        ? {
            '@type': 'Person',
            name: post.authors[0]?.name || '',
            image: post.authors[0]?.profile_picture?.url
              ? {
                  '@type': 'ImageObject',
                  contentUrl: post.authors[0]?.profile_picture?.url,
                  thumbnailUrl: post.authors[0]?.profile_picture?.thumb?.url,
                }
              : undefined,
          }
        : undefined,
      publisher: publication
        ? {
            '@type': 'Organization',
            name: publication?.name || '',
            url: publication?.url || '',
            identifier: publication?.id || '',
            sameAs: getSameAsArray(publication),
            logo: publication?.logo?.url
              ? {
                  '@type': 'ImageObject',
                  url: publication.logo.url,
                  contentUrl: publication.logo.url,
                  thumbnailUrl: publication.logo?.thumb?.url,
                }
              : undefined,
            image: publication?.logo?.url
              ? {
                  '@type': 'ImageObject',
                  url: publication.logo.url,
                  contentUrl: publication.logo.url,
                  thumbnailUrl: publication.logo?.thumb?.url,
                }
              : undefined,
          }
        : undefined,
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            item: {
              '@id': publication?.url || '',
              name: publication?.name || '',
            },
          },
          {
            '@type': 'ListItem',
            position: 2,
            item: {
              '@id': requestUrl,
              name: post?.web_title || '',
            },
          },
        ],
      },
    },
  });
};

export const headers: HeadersFunction = ({ loaderHeaders }) => {
  return {
    'cache-control': loaderHeaders.get('cache-control') || 'max-age=0', // no-cache
    'Cache-Tag': 'orchid-page',
  };
};

export interface PostLoaderData {
  post: IPost;
  postMergeTags?: PostMergeTags;
  dynamicContentConditions?: PostDynamicContentConditions;
  page: Page;
  site: Site;
  webTheme: WebThemeV2;
  publication: Publication;
  commentsEnabled: boolean;
  recommendedReadingEnabled: boolean;
  requestUrl: string;
  onlyRenderComments: boolean;
  isDraft: boolean;
  paywall: Paywall | null;
  currentSubscriberId: string;
  currentTierId: string;
  renderableFonts: string[];
  renderableFontsCss: GoogleFontEntryType[];
  subscription?: Subscription;
  previewSubscription?: Subscription;
  previewJwtToken?: string;
  postMetaInformation?: PostMetaInformation;
  // Content gate data
  isContentClipped?: boolean;
  clippedPageContent?: TRootElement | BasicNode[] | null;
}

export const loader: LoaderFunction = async ({ request, params }) => {
  const { session } = await loaderSession(request);
  const url = new URL(request.url);
  const showComments = url.searchParams.get('comments') === 'true';
  const isDraft = url.searchParams.get('draft') === 'true';
  const currentSubscriberId = url.searchParams.get('current_subscriber_id') || '';
  const currentTierId = url.searchParams.get('current_tier_id') || '';

  if (!params.slug) {
    return redirect('/');
  }

  const promises: [Promise<Publication>, Promise<Site>, Promise<IPost>] = [
    getPublication(request, session),
    getSite(request, session),
    isDraft
      ? getDraftPost({
          request,
          session,
          slug: params.slug || '',
          currentSubscriberId,
          currentTierId,
        })
      : getPost(request, session, params.slug || ''),
  ];
  const [publication, site, post] = await Promise.all(promises);

  const isPremiumPost = post.audience === 'premium' || post.audience === 'both';
  let paywall = null;
  if (isPremiumPost && !post.force_premium) {
    paywall = await getPaywall(request, session, post.paywall_id);
  }

  if (post.redirect) return redirect('/'); // Redirects to index feed when no post is found

  const page = await getPage(request, session, {
    pathname: 'p/$slug',
    postSlug: params.slug,
    draft: url.searchParams.get('draft') || undefined,
  });
  const renderableFontsCss = await getRenderableFontsCss(
    site,
    (page as Page) || undefined,
    post.post_theme as PostTheme,
  );

  // When previewing a post via Swarm, a jwt token is sent with payload tier_id or subscriber_id
  // Make a request to get a preview subscription based on the payload of the jwt token
  // The subscription in the response is used in `PostContent` component to
  // render the content based on visibility settings
  const previewJwtToken = url.searchParams.get('preview_jwt_token');
  let previewSubscription: Subscription | undefined;
  if (previewJwtToken && isDraft) {
    previewSubscription = await getPreviewSubscription(request, session, previewJwtToken);
  }

  return Response.json(
    {
      previewSubscription,
      previewJwtToken,
      post,
      postMergeTags: {},
      site,
      page,
      publication,
      commentsEnabled: post.comments_enabled,
      recommendedReadingEnabled: publication.web_template.enable_recommended_reading,
      requestUrl: url.href,
      onlyRenderComments: showComments,
      isDraft,
      paywall,
      currentSubscriberId,
      currentTierId,
      renderableFontsCss,
      postMetaInformation: {
        comments_enabled: post.comments_enabled,
        hide_premium_post: post.hide_premium_post,
        paywall_shown: post.paywall_shown,
        force_premium: post.force_premium,
        enforce_paywall_breaks: post.enforce_paywall_breaks,
        contains_subscriber_break: post.contains_subscriber_break,
      },
    },
    {
      // Note: set max-age to 3 to allow prefetch cachine before trying to get from CDN via s-max-age
      headers: setCacheHeaders({
        maxAge: 3,
        sMaxAge: 60 * 60 * 24 * 30, // 30 days
        staleWhileRevalidate: 60,
      }),
    },
  );
};

export const clientLoader = async ({ request, serverLoader }: ClientLoaderFunctionArgs) => {
  const serverData = await serverLoader<PostLoaderData>();
  if (!serverData?.site || !serverData?.publication || !serverData?.page || !serverData?.post) {
    // show the refresh page message if we didn't get server data
    throw new Error('RefreshPageError');
  }

  // Fetch merge tags for the post
  const postMergeTags = await fetchMergeTags({
    postId: serverData.post.id,
    isDraft: serverData.isDraft,
    previewJwtToken: serverData.previewJwtToken,
  });

  // Fetch evaluated dynamic content conditions for the post
  const dynamicContentConditions = await fetchDynamicContentConditions({
    postId: serverData.post.id,
    isDraft: serverData.isDraft,
    previewJwtToken: serverData.previewJwtToken,
  });

  // Fetch post meta information
  const postMetaInformation = await fetchPostMetaInformation({
    slug: serverData.post.slug,
    isDraft: serverData.isDraft,
    requestUrl: request.url,
    cookies: document.cookie,
    commentsEnabled: serverData.commentsEnabled,
    hidePremiumPost: serverData.post.hide_premium_post,
    paywallShown: serverData.post.paywall_shown,
    forcePremium: serverData.post.force_premium,
    enforcePaywallBreaks: serverData.post.enforce_paywall_breaks,
    containsSubscriberBreak: serverData.post.contains_subscriber_break,
  });

  // Process content gate
  const { isContentClipped, clippedPageContent, subscription } = await processContentGate({
    page: serverData.page,
    requestUrl: request.url,
    cookies: document.cookie,
  });

  return {
    ...serverData,
    post: {
      ...serverData.post,
    },
    postMetaInformation,
    postMergeTags,
    dynamicContentConditions,
    isContentClipped,
    clippedPageContent,
    subscription,
  };
};

clientLoader.hydrate = true;

export default function Post() {
  const {
    post,
    site,
    page,
    commentsEnabled,
    onlyRenderComments,
    isDraft,
    paywall,
    previewSubscription,
    postMetaInformation,
  } = useTypedLoaderData<PostLoaderData>();
  if (!site || !post) {
    // show the refresh page message if we didn't get server data
    throw new Error('RefreshPageError');
  }

  const { subscription } = useRouteLoaderData<typeof loader>('root');
  const { cookies, setCookie, cookiesSet } = useOutletContext<OutletContext>();

  // Create Page View
  const visitToken = cookies.visit_token ?? '';
  const pageViews = cookies.page_view ?? '';
  useCreatePageView({
    visitToken: visitToken,
    pageViews: pageViews || '',
    post,
    setCookie,
    cookiesSet,
  });

  useSetLastViewedResource({
    guid: `Post:${post.id}`,
    setCookie,
    cookiesSet,
  });

  // setup click tracking for links
  useClickTracking({ post, visitToken });

  return (
    <PostPageProvider
      post={post}
      site={site}
      hide_premium_post={postMetaInformation?.hide_premium_post}
      paywall_shown={postMetaInformation?.paywall_shown}
      force_premium={postMetaInformation?.force_premium}
      enforce_paywall_breaks={postMetaInformation?.enforce_paywall_breaks}
      contains_subscriber_break={postMetaInformation?.contains_subscriber_break}
      subscription={subscription}
      previewSubscription={previewSubscription}
      commentsEnabled={postMetaInformation?.comments_enabled ?? commentsEnabled}
      isDraft={isDraft}
      onlyRenderComments={onlyRenderComments}
      paywall={paywall}
    >
      <PostPageContext.Consumer>
        {(value) => {
          return (
            <>
              {!value?.onlyRenderComments && <StocktwitsEmbed />}
              <StaticPage site={site} page={page}>
                {!value?.onlyRenderComments && (
                  <>
                    <TwitterEmbed updateOn={value?.isPopupDisabled} />
                    <BlueskyEmbed updateOn />
                  </>
                )}
              </StaticPage>
            </>
          );
        }}
      </PostPageContext.Consumer>
    </PostPageProvider>
  );
}

export const shouldRevalidate = shouldRevalidateLoaderData;
